import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CustomCursor from '@/pages/home/components/CustomCursor';

const sections = [
  {
    title: '1. Introduction',
    content: [
      'This Privacy Policy explains how Syntera Solutions LTD ("Company", "we", "our", or "us") collects, uses, stores, and protects personal data when you use this website (the "Site") and any related services (collectively, the "Services").',
      'Registered Office:\nInternational House\n64 Nile Street\nLondon\nN1 7SR\nUnited Kingdom',
      'Email: help@syntera-solutions.com',
      'Phone: +44 7520 640897',
      'We are committed to protecting your privacy and handling your personal data in accordance with applicable data protection laws, including the UK GDPR.',
      'By using the Site, you acknowledge that you have read and understood this Privacy Policy.',
    ],
  },
  {
    title: '2. Information We Collect',
    content: [
      'We may collect the following categories of information:',
      'a. Personal Information\nName\nEmail address\nPhone number (if provided)\nAny information you submit via forms or communications',
      'b. Technical and Usage Data\nIP address\nBrowser type and version\nDevice type and operating system\nPages visited and interaction data\nDate and time of access',
      'c. Communication Data\nMessages sent through contact forms or email\nSupport requests and related correspondence',
      'd. Cookies and Tracking Technologies\nWe use cookies and similar technologies to improve functionality and user experience. See Section 9 for details.',
    ],
  },
  {
    title: '3. How We Use Your Information',
    content: [
      'We use your data for the following purposes:',
      'a. Providing Services\nResponding to inquiries and requests\nOperating and maintaining the Site\nDelivering requested information or services',
      'b. Improving the Site\nAnalyzing usage and performance\nEnhancing functionality and user experience',
      'c. Communication\nSending responses, updates, or important notices\nProviding support',
      'd. Legal Compliance\nComplying with legal obligations\nPreventing fraud or misuse\nEnforcing our Terms of Use',
      'e. Marketing (if applicable)\nSending updates or promotional content (only with your consent)\nYou may opt out at any time',
    ],
  },
  {
    title: '4. Legal Basis for Processing',
    content: [
      'We process personal data based on:',
      'Consent — where you have provided it',
      'Legitimate Interests — to operate and improve our services',
      'Legal Obligations — where required by law',
      'Contractual Necessity — where processing is required to respond to your request',
    ],
  },
  {
    title: '5. Sharing of Information',
    content: [
      'We may share your data with:',
      'a. Service Providers\nThird-party providers who assist with:\nWebsite hosting\nEmail communication\nAnalytics\nThese providers are required to protect your data and use it only for specified purposes.',
      'b. Legal Requirements\nWe may disclose information if required by law or to protect rights, safety, or property.',
      'c. Business Transfers\nIn the event of a merger or sale, data may be transferred to a new entity under similar protections.',
      'd. With Your Consent\nWe may share data where you explicitly agree.',
      'We do not sell personal data.',
    ],
  },
  {
    title: '6. Data Retention',
    content: [
      'We retain personal data only as long as necessary to:',
      'Fulfill the purposes described in this Policy',
      'Comply with legal obligations',
      'Resolve disputes',
      'Retention periods vary depending on the type of data.',
    ],
  },
  {
    title: '7. Data Security',
    content: [
      'We implement appropriate technical and organizational measures, including:',
      'Secure data storage and transmission',
      'Access controls',
      'Monitoring and protection against unauthorized access',
      'However, no system is completely secure, and we cannot guarantee absolute security.',
    ],
  },
  {
    title: '8. Your Rights',
    content: [
      'Under applicable data protection laws, you have the right to:',
      'Access your personal data',
      'Correct inaccurate data',
      'Request deletion of your data',
      'Restrict processing in certain cases',
      'Object to processing',
      'Request data portability',
      'To exercise your rights, contact:\nhelp@syntera-solutions.com',
    ],
  },
  {
    title: '9. Cookies',
    content: [
      'We use cookies to improve functionality and user experience.',
      'Types of cookies we use:',
      'Essential – required for operation of the Site',
      'Analytics – help us understand usage',
      'Functional – remember preferences',
      'You can manage or disable cookies through your browser settings.',
    ],
  },
  {
    title: '10. International Data Transfers',
    content: [
      'Your data may be processed outside your country of residence.',
      'Where required, we ensure appropriate safeguards are in place in accordance with applicable laws.',
    ],
  },
  {
    title: '11. Children\'s Privacy',
    content: [
      'The Site is not intended for individuals under the age of 16.',
      'We do not knowingly collect personal data from children. If we become aware of such data, we will delete it.',
    ],
  },
  {
    title: '12. Changes to this Privacy Policy',
    content: [
      'We may update this Privacy Policy from time to time.',
      'Updates will be posted on this page with a revised effective date. Continued use of the Site constitutes acceptance of the updated Policy.',
    ],
  },
  {
    title: '13. Contact Information',
    content: [
      'Registered Office:\nInternational House\n64 Nile Street\nLondon\nN1 7SR\nUnited Kingdom',
      'Email: help@syntera-solutions.com',
      'Phone: +44 7520 640897',
    ],
  },
];

export default function PrivacyPage() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <main className="w-full overflow-x-hidden bg-[#0d0d0d]">
      <CustomCursor />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-[60vh] flex items-end overflow-hidden pt-24">
        <span
          className="boldonse absolute select-none pointer-events-none"
          style={{
            fontSize: '40vw',
            lineHeight: 1,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -48%)',
            color: 'rgba(255,255,255,0.03)',
            letterSpacing: '-8px',
            whiteSpace: 'nowrap',
          }}
        >
          PRIVACY
        </span>

        <div className="relative z-10 px-10 lg:px-20 pt-32 pb-16 max-w-3xl">
          <div
            className={`transition-all duration-1000 delay-200 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <p className="text-[#F8CA9D] text-xs tracking-[0.5em] uppercase mb-8 font-medium">
              &#8212;&nbsp;&nbsp;Legal information&nbsp;&nbsp;&#8212;
            </p>
            <h1
              className="boldonse text-white leading-none mb-8"
              style={{ fontSize: 'clamp(56px, 8vw, 120px)', lineHeight: 0.9 }}
            >
              PRIVACY<br />
              <span style={{ color: '#FA6E4F' }}>POLICY.</span>
            </h1>
          </div>
          <div
            className={`transition-all duration-1000 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <p className="text-white/60 text-lg leading-relaxed max-w-md">
              This Privacy Policy explains how we collect, use, and protect your personal data when you use our services.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="px-10 lg:px-20 py-24 max-w-4xl mx-auto">
        <div className="flex flex-col gap-16">
          {sections.map((section, i) => (
            <div
              key={section.title}
              className={`transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${600 + i * 80}ms` }}
            >
              <h2 className="boldonse text-white text-2xl mb-6">{section.title}</h2>
              <div className="flex flex-col gap-3">
                {section.content.map((line, j) => (
                  <p
                    key={j}
                    className="text-white/70 text-base leading-relaxed whitespace-pre-line"
                  >
                    {line}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </main>
  );
}